import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import omit from 'lodash.omit';

import { Address } from '../../models';
import { AddressesActions, AddressesActionTypes } from './addresses.actions';

export interface State extends EntityState<Address> {
  selectedBilling: string | null;
  selectedShipping: string | null;
  loading: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingPreserved: boolean;
  successCreate: boolean;
  successUpdate: boolean;
  error: any;
  errorCreate: any;
  errorUpdate: any;
  errorPreserved: any;
  preserved: any;
  isShipping: boolean;
  freightForwarding: any;
  billingAsShipping: boolean;
  isShippingAddressValidationInProcess: boolean;
  isShippingAddressValidationSuccessful: boolean;
}

export const adapter: EntityAdapter<Address> = createEntityAdapter<Address>({
  sortComparer: false,
});

export const initialState: State = adapter.getInitialState({
  selectedBilling: null,
  selectedShipping: null,
  loading: true,
  loadingCreate: false,
  loadingUpdate: false,
  loadingPreserved: true,
  successCreate: false,
  successUpdate: false,
  error: null,
  errorUpdate: null,
  errorPreserved: null,
  errorCreate: null,
  preserved: null,
  isShipping: false,
  freightForwarding: null,
  billingAsShipping: true,
  isShippingAddressValidationInProcess: false,
  isShippingAddressValidationSuccessful: true
});

export function reducer(state = initialState, action: AddressesActions): State {
  switch (action.type) {
    case AddressesActionTypes.ValidateShippingAddress:
      return {
        ...state,
        isShippingAddressValidationInProcess: true
      };

    case AddressesActionTypes.ValidateShippingAddressSuccess:
      return {
        ...state,
        isShippingAddressValidationInProcess: false,
        isShippingAddressValidationSuccessful: true,
      };

    case AddressesActionTypes.ValidateShippingAddressFailure:
      return {
        ...state,
        isShippingAddressValidationInProcess: false,
        isShippingAddressValidationSuccessful: false,
      };

    case AddressesActionTypes.ClearAddresses:
      return {
        ...state,
        ids: [],
        entities: {},
        freightForwarding: null,
        selectedBilling: null,
        selectedShipping: null,
        preserved: null,
      };

    case AddressesActionTypes.ReadAddresses:
      return {
        ...state,
        successCreate: false,
        loading: true,
        error: null,
      };

    case AddressesActionTypes.ReadAddressesSuccess:
      const defaultBillingAddress = action.payload.find((item) => item.defaultBilling === true);
      const defaultShippingAddress = action.payload.find((item) => item.defaultShipping === true);
      const selectedShipping = defaultShippingAddress ? defaultShippingAddress.id : null;

      return adapter.setAll(action.payload, {
        ...state,
        loading: false,
        selectedBilling: defaultBillingAddress ? defaultBillingAddress.id : selectedShipping,
        selectedShipping,
      });

    case AddressesActionTypes.ReadAddressesFailure:
      return adapter.removeAll({
        ...state,
        loading: false,
        error: action.payload,
      });

    case AddressesActionTypes.SelectBillingAddress:
      return {
        ...state,
        selectedBilling: action.payload.id,
      };

    case AddressesActionTypes.SelectShippingAddress:
      return {
        ...state,
        selectedShipping: action.payload.id,
      };

    case AddressesActionTypes.UpdatePreservedAddress:
      return {
        ...state,
        preserved: { billing: action.payload.billing, shipping: action.payload.shipping },
        billingAsShipping: action.payload.billingAsShipping,
        loadingPreserved: false,
      };

    case AddressesActionTypes.UpdateFreightForwarding:
      return {
        ...state,
        freightForwarding: action.payload,
      };

    case AddressesActionTypes.RemovePreservedAddress:
      return {
        ...state,
        entities: omit(state.entities, action.payload.addressId),
        preserved: null,
      };

    case AddressesActionTypes.CreateAddress:
    case AddressesActionTypes.CreateAddressBatch:
      return {
        ...state,
        loadingCreate: true,
        successCreate: false,
        errorCreate: null,
      };

    case AddressesActionTypes.CreateAddressSuccess:
      return {
        ...state,
        isShipping: action.payload.defaultShipping,
        successCreate: true,
        loadingCreate: false,
      };

    case AddressesActionTypes.CreateAddressFailure:
      return {
        ...state,
        successCreate: false,
        loadingCreate: false,
        errorCreate: action.payload,
      };

    case AddressesActionTypes.CreateAddressBatchSuccess:
      return {
        ...state,
        isShipping: undefined,
        successCreate: true,
        loadingCreate: false,
      };

    case AddressesActionTypes.CreateAddressBatchFailure:
      return {
        ...state,
        successCreate: false,
        loadingCreate: false,
        errorCreate: action.payload.error,
      };

    case AddressesActionTypes.UpdateAddress:
      return {
        ...state,
        entities: {
          ...state.entities,
          [action.payload.id]: action.payload.address,
        },
        loadingUpdate: true,
        successUpdate: false,
        errorUpdate: null,
      };

    case AddressesActionTypes.UpdateAddressSuccess:
      return {
        ...state,
        successUpdate: true,
        loadingUpdate: false,
      };

    case AddressesActionTypes.UpdateAddressFailure:
      return {
        ...state,
        successUpdate: false,
        loadingUpdate: false,
        errorUpdate: action.payload,
      };

    default:
      return state;
  }
}

const { selectAll } = adapter.getSelectors();

export const selectAllAddresses = selectAll;
